import { FaFacebook, FaTwitter, FaInstagram } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6";
import { Link } from "react-router";
import blidzTextImg from "~/assets/blidz-text.svg";
import appStoreImg from "~/assets/get-it-on-app-store.svg";
import googlePlayImg from "~/assets/get-it-on-google-play.svg";
import blidzQrImg from "~/assets/blidz-qr.png";
import footerFaceImg from "~/assets/deal-feed/footer-face.svg";
// Payment method images
import visaImg from "~/assets/payment-methods/visa.svg";
import dinersClubImg from "~/assets/payment-methods/diners-club.svg";
import amexImg from "~/assets/payment-methods/amex.svg";
import discoverImg from "~/assets/payment-methods/discover.svg";
import mastercardImg from "~/assets/payment-methods/mastercard.svg";
// Security certification images
import visaSecureImg from "~/assets/security-certification/visa.png";
import mastercardSecureImg from "~/assets/security-certification/mastercard.png";
import americanExpressSecureImg from "~/assets/security-certification/american-express.png";
import discoverSecureImg from "~/assets/security-certification/discover.png";
import stripeImg from "~/assets/security-certification/stripe.png";
import pciImg from "~/assets/security-certification/pci.png";
import sectigoImg from "~/assets/security-certification/sectigo.png";
import { APPSTORE_LINK, PLAYSTORE_LINK } from "~/constants";
import { cn } from "~/lib/utils";

const FOOTER_BACKGROUND_IMAGE_CLASS = "bg-[url('https://asset.blidz.com/blidz-cdn/web/footer/footer-bg.jpg')]";

// Data constants for better maintainability
const SUPPORT_LINKS = [
  {
    name: "Terms",
    href: "/profile/terms-of-service",
  },
  {
    name: "Shipping",
    href: "/profile/shipping-policy",
  },
  {
    name: "Returns",
    href: "/profile/return-policy",
  },
  {
    name: "Privacy Policy",
    href: "/profile/privacy-policy",
  },
  {
    name: "Billing terms",
    href: "/profile/billing-terms",
  },
];

const PAYMENT_METHODS = [
  { name: "Visa", src: visaImg },
  { name: "Diners Club", src: dinersClubImg },
  { name: "Amex", src: amexImg },
  { name: "Discover", src: discoverImg },
  { name: "Mastercard", src: mastercardImg },
];

const SECURITY_CERTS = [
  { name: "Visa Secure", src: visaSecureImg },
  { name: "Mastercard", src: mastercardSecureImg },
  { name: "American Express", src: americanExpressSecureImg },
  { name: "Discover", src: discoverSecureImg },
  { name: "Stripe", src: stripeImg },
  { name: "PCI", src: pciImg },
  { name: "Sectigo", src: sectigoImg },
];

const SOCIAL_ICONS = [
  { Icon: FaFacebook, href: "https://www.facebook.com/BlidzOfficial" },
  { Icon: FaTwitter, href: "https://x.com/BlidzOfficial" },
  { Icon: FaInstagram, href: "https://www.instagram.com/BlidzOfficial/" },
  { Icon: FaTiktok, href: "https://www.tiktok.com/@blidz_official" },
];

export default function Footer() {
  return (
    <footer>
      {/* Main Footer Content */}
      <div className={cn("relative overflow-hidden", "bg-cover bg-center bg-no-repeat", FOOTER_BACKGROUND_IMAGE_CLASS)}>
        <div className="relative z-10 container mx-auto flex flex-col items-center justify-between gap-6 px-10 py-9 md:flex-row md:items-start">
          {/* Left Section - Logo, App Downloads, and QR Code */}
          <div className="flex-shrink-0">
            <div className="flex items-end gap-4 lg:gap-6">
              {/* Column 1: Logo, Tagline, and App Download Buttons */}
              <div className="flex flex-col justify-between">
                {/* Logo and Tagline */}
                <div className="mb-6">
                  <img src={blidzTextImg} alt="Blidz" className="mb-2.5 h-7" />
                  <p className="text-sm font-medium text-black">Make Money, Save Money, Have Fun</p>
                </div>

                {/* App Download Buttons */}
                <div className="flex gap-2.5">
                  <a href={APPSTORE_LINK} className="block" target="_blank">
                    <img src={appStoreImg} alt="Download on App Store" className="h-11" />
                  </a>
                  <a href={PLAYSTORE_LINK} className="block" target="_blank">
                    <img src={googlePlayImg} alt="Get it on Google Play" className="h-11" />
                  </a>
                </div>
              </div>

              {/* Column 2: QR Code */}
              <img src={blidzQrImg} alt="QR Code" className="h-34 w-34 rounded-xl border border-gray-800" />
            </div>
          </div>

          {/* Right Section - Navigation Links, Payment, and Security */}
          <div className="flex self-start lg:max-w-2xl lg:flex-1 lg:justify-end">
            <div className="flex flex-col gap-5">
              {/* Navigation Columns */}
              <div className="flex flex-row gap-6">
                {/* Support Column */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-base font-bold whitespace-nowrap text-gray-900">Support</h3>
                  <div className="flex flex-col gap-3 text-xs font-medium whitespace-nowrap text-gray-600">
                    {SUPPORT_LINKS.map(item => (
                      <Link key={item.name} to={item.href} className="hover:text-gray-900">
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Company Column */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-base font-bold whitespace-nowrap text-black">Company</h3>
                  <div className="flex flex-col gap-2 text-xs font-medium text-gray-600">
                    <p className="whitespace-nowrap">
                      Email support:{" "}
                      <a href="mailto:support@blidz.com" className="hover:underline">
                        support@blidz.com
                      </a>
                    </p>
                    <p className="whitespace-nowrap">
                      Phone support:{" "}
                      <a href="tel:+19176727658" className="hover:underline">
                        +1 917 6727658
                      </a>
                    </p>
                    <p className="max-w-48 break-words">
                      Address: 30 Broad Street, 14th Floor Suite #1469, New York City, NY 10004
                    </p>
                  </div>

                  {/* Social Icons */}
                  <div className="mt-2 flex items-center gap-3.5 text-black">
                    {SOCIAL_ICONS.map(({ Icon, href }, index) => (
                      <a key={index} href={href} className="h-4 w-4 hover:text-gray-600" target="_blank">
                        <Icon size={16} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Payment and Security Section */}
              <div className="flex flex-col gap-2.5">
                {/* We Accept Payment Methods */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-semibold text-gray-900">We accept</h4>
                  <div className="flex flex-wrap gap-2">
                    {PAYMENT_METHODS.map(payment => (
                      <img key={payment.name} src={payment.src} alt={payment.name} className="h-6 w-9 object-contain" />
                    ))}
                  </div>
                </div>

                {/* Security Certification */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-semibold text-gray-900">Security certification</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {SECURITY_CERTS.map(cert => (
                      <img
                        key={cert.name}
                        src={cert.src}
                        alt={cert.name}
                        className="h-5 rounded-xs border border-gray-200 object-contain"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Background Elements */}
        <img
          src={footerFaceImg}
          alt=""
          className="pointer-events-none absolute bottom-0 left-0 hidden h-52 w-120 object-cover md:block"
        />
      </div>

      {/* Sub-footer */}
      <div className="border-t border-black/10 bg-black shadow-sm backdrop-blur-sm">
        <div className="container mx-auto flex justify-center px-10 py-2">
          <a href="#" className="text-xs font-semibold text-white hover:text-gray-200">
            Get Blidz App for discount perks and <span className="text-yellow-400">$30</span> Cash
          </a>
        </div>
      </div>
    </footer>
  );
}
